import React, {
  startTransition,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ComparisonDetailPage,
  ComparisonsPage,
  EditorialHubPage,
  EntityEditorialSection,
  ListicleDetailPage,
  ListiclesPage,
  ReviewDetailPage,
  ReviewsPage,
} from "./EditorialPages";
import ApprovedOpinionList from "./ApprovedOpinionList";
import OpinionForm from "./OpinionForm";
import brandLogo from "./cad-logo.svg";

const API_BASE = process.env.REACT_APP_API_BASE || "/api/public";
const DEFAULT_DESCRIPTION =
  "Explorá instituciones, carreras y cursos online en un directorio preparado para crecer con miles de programas.";

function buildApiUrl(pathname, params = {}) {
  const url = new URL(`${API_BASE}${pathname}`, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.pathname + url.search;
}

async function fetchJson(pathname, params = {}, signal) {
  const response = await fetch(buildApiUrl(pathname, params), { signal });
  const payload = await response.json();

  if (!response.ok || !payload.success) {
    throw new Error(payload.message || "No se pudo obtener la información.");
  }

  return payload;
}

function clampPage(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return parsed;
}

function excerpt(value, limit = 170) {
  const text = String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text || text.length <= limit) {
    return text;
  }

  const slice = text.slice(0, limit);
  const lastSpace = slice.lastIndexOf(" ");
  const safeSlice = lastSpace > 80 ? slice.slice(0, lastSpace) : slice;

  return `${safeSlice.replace(/[ ,.;:-]+$/, "")}...`;
}

function humanizeSlug(value) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function buildProgramMetaDescription(program) {
  if (!program) {
    return DEFAULT_DESCRIPTION;
  }

  const parts = [
    program.title,
    program.institution?.name || program.institution_name,
    program.modality,
    program.duration,
    program.level,
  ].filter(Boolean);

  return excerpt(`${parts.join(". ")}. Consultá requisitos, certificaciones y enfoque del programa.`, 158);
}

function buildProgramImageAlt(program) {
  if (!program) {
    return "Imagen del programa";
  }

  const parts = [
    `Programa ${program.title}`,
    program.institution?.name || program.institution_name,
    program.area,
    program.modality,
  ].filter(Boolean);

  return parts.join(" | ");
}

function buildProgramFaqItems(program) {
  if (!program) {
    return [];
  }

  return [
    {
      question: `¿Qué tipo de formación es ${program.title}?`,
      answer: `${program.title} figura en el directorio como una propuesta ${program.level || "académica"} con modalidad ${program.modality || "online"}${program.duration ? ` y una duración estimada de ${program.duration}` : ""}.`,
    },
    {
      question: "¿Qué conviene revisar antes de inscribirse?",
      answer: `Antes de avanzar, conviene confirmar requisitos, certificaciones, modalidad de cursada y detalles del plan de estudios directamente con ${program.institution?.name || program.institution_name || "la institución"}.`,
    },
    {
      question: "¿Dónde comparar este programa con otras alternativas?",
      answer: `Podés revisar más opciones dentro de ${program.area || "la misma área"}, explorar otros programas de ${program.institution?.name || program.institution_name || "la institución"} o usar el comparador para ordenar la decisión.`,
    },
  ];
}

function normalizeCommunityRating(rating) {
  const average = Number.parseFloat(rating?.average);
  const count = Number.parseInt(rating?.count, 10);

  if (!Number.isFinite(average) || !Number.isInteger(count) || count < 1) {
    return null;
  }

  return {
    average: Math.max(0, Math.min(5, average)),
    count,
  };
}

function formatCommunityRatingAverage(value) {
  return value.toFixed(1);
}

function formatCommunityRatingCount(count, includeApprovedLabel = false) {
  const noun = count === 1 ? "opinion" : "opiniones";
  if (!includeApprovedLabel) {
    return `${count} ${noun}`;
  }

  const adjective = count === 1 ? "aprobada" : "aprobadas";
  return `Basada en ${count} ${noun} ${adjective}.`;
}

function getInstitutionPath(institution) {
  if (!institution) {
    return "/instituciones";
  }

  if (institution.path) {
    return institution.path;
  }

  if (institution.institution_path) {
    return institution.institution_path;
  }

  if (institution.slug) {
    return `/${institution.slug}`;
  }

  if (institution.institution_slug) {
    return `/${institution.institution_slug}`;
  }

  return `/instituciones/${institution.id}`;
}

function getProgramPath(program) {
  if (!program) {
    return "/programa";
  }

  if (program.path) {
    return program.path;
  }

  if (program.institution_slug && program.slug) {
    return `/${program.institution_slug}/${program.slug}`;
  }

  return `/programa/${encodeURIComponent(program.id)}`;
}

function useDocumentMeta(title, description) {
  useLayoutEffect(() => {
    const previousTitle = document.title;
    const descriptionNode = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionNode ? descriptionNode.getAttribute("content") : null;
    const canonicalHref = `${window.location.origin}${window.location.pathname}`;
    let canonicalNode = document.querySelector('link[rel="canonical"]');
    const createdCanonicalNode = !canonicalNode;
    const previousCanonical = canonicalNode ? canonicalNode.getAttribute("href") : null;

    if (!canonicalNode) {
      canonicalNode = document.createElement("link");
      canonicalNode.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalNode);
    }

    document.title = title;
    if (descriptionNode) {
      descriptionNode.setAttribute("content", description);
    }
    if (canonicalNode) {
      canonicalNode.setAttribute("href", canonicalHref);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionNode && previousDescription !== null) {
        descriptionNode.setAttribute("content", previousDescription);
      }
      if (canonicalNode) {
        if (createdCanonicalNode) {
          canonicalNode.remove();
        } else if (previousCanonical !== null) {
          canonicalNode.setAttribute("href", previousCanonical);
        }
      }
    };
  }, [description, title]);
}

function useStructuredData(identifier, payload) {
  const serializedPayload = payload ? JSON.stringify(payload) : "";

  useLayoutEffect(() => {
    const selector = `script[data-structured-data="${identifier}"]`;
    const existingNode = document.querySelector(selector);

    if (!serializedPayload) {
      if (existingNode) {
        existingNode.remove();
      }
      return undefined;
    }

    const scriptNode = existingNode || document.createElement("script");
    scriptNode.setAttribute("type", "application/ld+json");
    scriptNode.setAttribute("data-structured-data", identifier);
    scriptNode.textContent = serializedPayload;

    if (!existingNode) {
      document.head.appendChild(scriptNode);
    }

    return () => {
      const currentNode = document.querySelector(selector);
      if (currentNode) {
        currentNode.remove();
      }
    };
  }, [identifier, serializedPayload]);
}

function useApiData(pathname, params) {
  const [state, setState] = useState({
    data: null,
    meta: null,
    loading: true,
    error: "",
  });
  const requestKey = JSON.stringify(params);
  const stableParams = useMemo(() => JSON.parse(requestKey), [requestKey]);

  useEffect(() => {
    if (stableParams === null) {
      setState({
        data: null,
        meta: null,
        loading: false,
        error: "",
      });
      return undefined;
    }

    const controller = new AbortController();

    setState((current) => ({
      data: current.data,
      meta: current.meta,
      loading: true,
      error: "",
    }));

    fetchJson(pathname, stableParams, controller.signal)
      .then((payload) => {
        setState({
          data: payload.data,
          meta: payload.meta || null,
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        setState({
          data: null,
          meta: null,
          loading: false,
          error: error.message,
        });
      });

    return () => controller.abort();
  }, [pathname, stableParams]);

  return state;
}

function useCatalogMeta() {
  return useApiData("/meta", {});
}

function Shell({ children }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link className="brand" to="/">
          <img className="brand-logo" src={brandLogo} alt="Cursos a Distancia" />
          <span className="brand-copy">
            <strong>Encontrá el curso exacto que buscás</strong>
            <small>Entre miles de opciones, con filtros por área, nivel y plataforma.</small>
          </span>
        </Link>
        <nav className="nav">
          <NavLink to="/" end>
            Programas
          </NavLink>
          <NavLink to="/instituciones">Instituciones</NavLink>
          <NavLink to="/paises">Países</NavLink>
          <NavLink to="/comparador">Comparador</NavLink>
          <NavLink to="/blog">Blog</NavLink>
        </nav>
      </header>
      <main>{children}</main>
      <footer className="site-footer">
        <div className="site-footer-grid">
          <div className="site-footer-copy">
            <strong>Cursos a Distancia</strong>
            <p>
              Directorio para comparar programas, instituciones y modalidades online en un solo
              lugar, con filtros claros para encontrar una opción más cercana a lo que necesitás.
            </p>
            <p>
              Las inscripciones y condiciones finales dependen de cada institución. CAD te ayuda a
              ordenar la búsqueda antes de ir a la fuente oficial.
            </p>
          </div>
          <nav className="site-footer-links" aria-label="Navegación del pie">
            <Link to="/">Programas</Link>
            <Link to="/instituciones">Instituciones</Link>
            <Link to="/comparador">Comparador</Link>
            <Link to="/blog">Blog</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Hero({ eyebrow, title, description, aside }) {
  return (
    <section className="hero">
      <div className="hero-copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <aside className="hero-aside">{aside}</aside>
    </section>
  );
}

function StatCard({ label, value }) {
  return (
    <article className="stat-card">
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function QueryInput({ label, value, onChange, placeholder }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange, emptyLabel }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{emptyLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function FiltersBar({ children }) {
  return <section className="filters-bar">{children}</section>;
}

function EmptyState({ title, description, action }) {
  return (
    <section className="empty-state">
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </section>
  );
}

function LoadingBlock({ label = "Cargando información..." }) {
  return (
    <section className="loading-block">
      <div className="spinner" />
      <p>{label}</p>
    </section>
  );
}

function ErrorBlock({ message }) {
  return (
    <section className="error-block">
      <h2>No pudimos cargar esta sección</h2>
      <p>{message}</p>
    </section>
  );
}

function Pagination({ meta, onPageChange }) {
  if (!meta || meta.total_pages <= 1) {
    return null;
  }

  const pages = [];
  const start = Math.max(1, meta.page - 2);
  const end = Math.min(meta.total_pages, meta.page + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return (
    <nav className="pagination" aria-label="Paginación">
      <button
        type="button"
        onClick={() => onPageChange(meta.page - 1)}
        disabled={meta.page <= 1}
      >
        Anterior
      </button>
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          className={page === meta.page ? "is-active" : ""}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onPageChange(meta.page + 1)}
        disabled={meta.page >= meta.total_pages}
      >
        Siguiente
      </button>
    </nav>
  );
}

function ProgramCard({ program, action }) {
  const communityRating = normalizeCommunityRating(program?.community_rating);

  return (
    <article className="card program-card">
      <div className="card-media">
        <img src={program.imageUrl} alt={program.title} loading="lazy" />
      </div>
      <div className="card-body">
        <div className="card-tags">
          <span>{program.area || "Programa"}</span>
          <span>{program.level || "Formación"}</span>
        </div>
        {communityRating ? (
          <div className="program-rating program-rating-badge" data-testid="program-rating-badge">
            <span className="program-rating-label">Calificación de la comunidad</span>
            <div className="program-rating-row">
              <strong className="program-rating-value">
                {formatCommunityRatingAverage(communityRating.average)}/5
              </strong>
              <span className="program-rating-count">
                {formatCommunityRatingCount(communityRating.count)}
              </span>
            </div>
          </div>
        ) : null}
        <h3>
          <Link to={getProgramPath(program)}>{program.title}</Link>
        </h3>
        <p>{program.description_excerpt}</p>
        <dl className="mini-meta">
          <div>
            <dt>Institución</dt>
            <dd>
              <Link to={getInstitutionPath(program)}>{program.institution_name}</Link>
            </dd>
          </div>
          <div>
            <dt>Modalidad</dt>
            <dd>{program.modality || "En línea"}</dd>
          </div>
          <div>
            <dt>Duración</dt>
            <dd>{program.duration || "Consultar"}</dd>
          </div>
          <div>
            <dt>Precio</dt>
            <dd>{program.price || "Consultar"}</dd>
          </div>
        </dl>
      </div>
      <div className="card-footer">
        <Link className="button button-primary" to={getProgramPath(program)}>
          Ver programa
        </Link>
        {action}
      </div>
    </article>
  );
}

function InstitutionCard({ institution }) {
  return (
    <article className="card institution-card">
      <div className="card-media">
        <img src={institution.imageUrl} alt={institution.name} loading="lazy" />
      </div>
      <div className="card-body">
        <div className="card-tags">
          <span>{institution.country || "Global"}</span>
          <span>{institution.programs_count} programas</span>
        </div>
        <h3>
          <Link to={getInstitutionPath(institution)}>{institution.name}</Link>
        </h3>
        <p>{institution.description_excerpt}</p>
        <dl className="mini-meta">
          <div>
            <dt>Ubicación</dt>
            <dd>{institution.location || "En línea"}</dd>
          </div>
          <div>
            <dt>Sitio web</dt>
            <dd>{institution.website || "Consultar"}</dd>
          </div>
        </dl>
      </div>
      <div className="card-footer">
        <Link className="button button-primary" to={getInstitutionPath(institution)}>
          Ver institución
        </Link>
      </div>
    </article>
  );
}

function CountryCard({ country }) {
  return (
    <article className="card country-card">
      <div className="card-media">
        <img src={country.imageUrl} alt={country.name} loading="lazy" />
      </div>
      <div className="card-body">
        <div className="card-tags">
          <span>{country.institutions_count} instituciones</span>
          <span>{country.programs_count} programas</span>
        </div>
        <h3>
          <Link to={`/paises/${country.slug}`}>{country.name}</Link>
        </h3>
        <p>{country.name} dentro del catálogo público y sus programas indexables.</p>
      </div>
      <div className="card-footer">
        <Link className="button button-primary" to={`/paises/${country.slug}`}>
          Explorar país
        </Link>
      </div>
    </article>
  );
}

function Grid({ children }) {
  return <section className="grid-layout">{children}</section>;
}

function updateSearchParams(currentSearchParams, updates = {}, resetPage = true) {
  const nextParams = new URLSearchParams(currentSearchParams);

  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
  });

  if (resetPage) {
    nextParams.set("page", "1");
  }

  return nextParams;
}

function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const metaState = useCatalogMeta();

  const page = clampPage(searchParams.get("page"), 1);
  const country = searchParams.get("country") || "";
  const area = searchParams.get("area") || "";
  const level = searchParams.get("level") || "";
  const qParam = searchParams.get("q") || "";
  const [searchDraft, setSearchDraft] = useState(qParam);
  const deferredSearch = useDeferredValue(searchDraft);

  useEffect(() => {
    setSearchDraft(qParam);
  }, [qParam]);

  useEffect(() => {
    if (deferredSearch === qParam) {
      return;
    }

    startTransition(() => {
      setSearchParams(updateSearchParams(searchParams, { q: deferredSearch }));
    });
  }, [deferredSearch, qParam, searchParams, setSearchParams]);

  const programsState = useApiData("/programs", {
    page,
    page_size: 12,
    q: qParam,
    country,
    area,
    level,
  });

  const filters = metaState.data?.filters || { countries: [], areas: [], levels: [] };

  useDocumentMeta(
    "Cursos a Distancia en Argentina | Directorio de cursos online",
    "Encontrar instituciones, carreras y cursos online en Argentina. Compara modalidades, niveles, areas y precios en un directorio publico preparado para crecer."
  );

  const updateParam = (key, value) => {
    setSearchParams(updateSearchParams(searchParams, { [key]: value }));
  };

  const resetFilters = () => {
    setSearchDraft("");
    setSearchParams({});
  };

  return (
    <div className="page">
      <Hero
        eyebrow="Programas"
        title="Cursos online y programas a distancia"
        description="Encontrá el curso exacto que buscás entre miles de opciones, con filtros por área, nivel y plataforma."
        aside={
          <div className="stats-grid">
            <StatCard label="Instituciones" value={metaState.data?.counts?.institutions || "-"} />
            <StatCard label="Programas" value={metaState.data?.counts?.programs || "-"} />
            <StatCard label="Países" value={metaState.data?.counts?.countries || "-"} />
          </div>
        }
      />

      <FiltersBar>
        <QueryInput
          label="Buscar"
          value={searchDraft}
          onChange={setSearchDraft}
          placeholder="Título, institución o área"
        />
        <SelectField
          label="País"
          value={country}
          options={filters.countries || []}
          onChange={(value) => updateParam("country", value)}
          emptyLabel="Todos los países"
        />
        <SelectField
          label="Área"
          value={area}
          options={filters.areas || []}
          onChange={(value) => updateParam("area", value)}
          emptyLabel="Todas las áreas"
        />
        <SelectField
          label="Nivel"
          value={level}
          options={filters.levels || []}
          onChange={(value) => updateParam("level", value)}
          emptyLabel="Todos los niveles"
        />
        <button className="button button-secondary" type="button" onClick={resetFilters}>
          Limpiar filtros
        </button>
      </FiltersBar>

      {programsState.loading && !programsState.data ? <LoadingBlock /> : null}
      {programsState.error ? <ErrorBlock message={programsState.error} /> : null}
      {programsState.data && programsState.data.length === 0 ? (
        <EmptyState
          title="No encontramos programas para esa combinación"
          description="Probá con menos filtros o cambiá el término de búsqueda."
          action={
            <button className="button button-primary" type="button" onClick={resetFilters}>
              Ver todo el catálogo
            </button>
          }
        />
      ) : null}
      {programsState.data && programsState.data.length > 0 ? (
        <>
          <section className="results-bar">
            <p>
              {programsState.meta?.total || 0} programas encontrados. Página{" "}
              {programsState.meta?.page || 1} de {programsState.meta?.total_pages || 1}.
            </p>
          </section>
          <Grid>
            {programsState.data.map((program) => (
              <ProgramCard key={program.id} program={program} />
            ))}
          </Grid>
          <Pagination
            meta={programsState.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : null}

      <section className="section-heading home-section-heading">
        <div>
          <span className="eyebrow">Elegir mejor</span>
          <h2>Información útil para encontrar una opción que encaje con vos</h2>
        </div>
        <p>
          Además del directorio, la home ahora explica cómo comparar programas, qué revisar antes
          de inscribirte y dónde profundizar según tu búsqueda.
        </p>
      </section>

      <section className="detail-grid home-trust-grid">
        <article className="panel">
          <h2>Sobre CAD</h2>
          <p>
            Cursos a Distancia reúne instituciones, plataformas y programas online en un solo lugar
            para que puedas comparar alternativas sin perderte entre pestañas, anuncios y sitios
            dispersos.
          </p>
          <p>
            La idea es simple: ayudarte a pasar de la exploración a una decisión concreta con
            filtros claros, fichas comparables y acceso rápido a la información clave.
          </p>
          <div className="link-stack">
            <Link to="/">Explorar programas</Link>
            <Link to="/instituciones">Ver instituciones</Link>
            <Link to="/blog">Leer el blog</Link>
          </div>
        </article>

        <article className="panel">
          <h2>Qué podés filtrar</h2>
          <ul className="rich-list">
            <li>Área para separar tecnología, negocios, salud, idiomas y otras disciplinas.</li>
            <li>Nivel para distinguir cursos introductorios, especializaciones y trayectos avanzados.</li>
            <li>País y plataforma para priorizar certificaciones, modalidad y tipo de institución.</li>
          </ul>
        </article>
      </section>

      <section className="comparison-table home-guides-grid">
        <article className="comparison-card">
          <span className="eyebrow">Guía rápida</span>
          <h3>Cómo elegir un curso online</h3>
          <p>
            Definí primero tu objetivo: aprender una habilidad puntual, sumar una certificación o
            cambiar de perfil profesional. Después compará duración, modalidad y respaldo
            institucional.
          </p>
          <Link className="button button-ghost" to="/blog">
            Leer guía para elegir un curso
          </Link>
        </article>

        <article className="comparison-card">
          <span className="eyebrow">Antes de inscribirte</span>
          <h3>Qué conviene revisar</h3>
          <p>
            Mirá el temario, la carga horaria, el tipo de acompañamiento, el certificado final y si
            la propuesta encaja con tu tiempo disponible y tu presupuesto.
          </p>
          <Link className="button button-ghost" to="/comparador">
            Comparar programas lado a lado
          </Link>
        </article>

        <article className="comparison-card">
          <span className="eyebrow">Exploración</span>
          <h3>Empezá por la categoría correcta</h3>
          <p>
            Si ya sabés el tema, podés entrar directo al listado filtrado por {" "}
            <Link to="/?area=Tecnología">tecnología</Link>, {" "}
            <Link to="/?area=Negocios">negocios</Link> o {" "}
            <Link to="/?area=Idiomas">idiomas</Link> y reducir la búsqueda desde el inicio.
          </p>
          <Link className="button button-ghost" to="/instituciones">
            Explorar instituciones destacadas
          </Link>
        </article>
      </section>
    </div>
  );
}

function InstitutionsPage() {
  const metaState = useCatalogMeta();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = clampPage(searchParams.get("page"), 1);
  const country = searchParams.get("country") || "";
  const qParam = searchParams.get("q") || "";
  const [searchDraft, setSearchDraft] = useState(qParam);
  const deferredSearch = useDeferredValue(searchDraft);

  useEffect(() => {
    setSearchDraft(qParam);
  }, [qParam]);

  useEffect(() => {
    if (deferredSearch === qParam) {
      return;
    }

    startTransition(() => {
      setSearchParams(updateSearchParams(searchParams, { q: deferredSearch }));
    });
  }, [deferredSearch, qParam, searchParams, setSearchParams]);

  const institutionsState = useApiData("/institutions", {
    page,
    page_size: 12,
    q: qParam,
    country,
  });

  useDocumentMeta(
    "Instituciones de cursos online | Cursos a Distancia",
    "Explora universidades, academias y plataformas online con fichas indexables y programas vinculados por pais."
  );

  return (
    <div className="page">
      <Hero
        eyebrow="Instituciones"
        title="Instituciones de cursos online"
        description="Cada ficha se resuelve por ID y cada listado filtra un catalogo extenso de instituciones."
        aside={
          <div className="stats-grid">
            <StatCard label="Instituciones" value={metaState.data?.counts?.institutions || "-"} />
            <StatCard label="Países" value={metaState.data?.counts?.countries || "-"} />
          </div>
        }
      />

      <FiltersBar>
        <QueryInput
          label="Buscar institución"
          value={searchDraft}
          onChange={setSearchDraft}
          placeholder="Nombre o ubicación"
        />
        <SelectField
          label="País"
          value={country}
          options={metaState.data?.filters?.countries || []}
          onChange={(value) => setSearchParams(updateSearchParams(searchParams, { country: value }))}
          emptyLabel="Todos los países"
        />
      </FiltersBar>

      {institutionsState.loading && !institutionsState.data ? <LoadingBlock /> : null}
      {institutionsState.error ? <ErrorBlock message={institutionsState.error} /> : null}
      {institutionsState.data && institutionsState.data.length > 0 ? (
        <>
          <section className="results-bar">
            <p>
              {institutionsState.meta?.total || 0} instituciones encontradas. Página{" "}
              {institutionsState.meta?.page || 1} de {institutionsState.meta?.total_pages || 1}.
            </p>
          </section>
          <Grid>
            {institutionsState.data.map((institution) => (
              <InstitutionCard key={institution.id} institution={institution} />
            ))}
          </Grid>
          <Pagination
            meta={institutionsState.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : null}
    </div>
  );
}

function DetailHeader({ title, eyebrow, description, aside }) {
  return (
    <section className="detail-hero">
      <div className="detail-copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <aside className="detail-aside">{aside}</aside>
    </section>
  );
}

function InstitutionDetailPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = clampPage(searchParams.get("page"), 1);
  const institutionKey = params.institutionSlug || params.id;
  const institutionLabel = humanizeSlug(institutionKey);
  const institutionState = useApiData(`/institutions/${encodeURIComponent(institutionKey)}`, {});
  const institution = institutionState.data;
  const programsState = useApiData(
    "/programs",
    institution
      ? {
          institution_id: String(institution.id),
          page,
          page_size: 6,
        }
      : null
  );

  useEffect(() => {
    if (institution?.path && location.pathname !== institution.path) {
      navigate(institution.path + location.search, { replace: true });
    }
  }, [institution, location.pathname, location.search, navigate]);

  useDocumentMeta(
    institution
      ? `${institution.name} | Cursos a Distancia`
      : `${institutionLabel || "Institucion"} | Cursos a Distancia`,
    institution
      ? excerpt(institution.description || institution.description_excerpt || "", 155)
      : `Explorá ${institutionLabel || "esta institución"} y sus programas online en el directorio.`
  );

  if (institutionState.loading && !institution) {
    return <LoadingBlock label="Cargando institución..." />;
  }

  if (institutionState.error) {
    return <ErrorBlock message={institutionState.error} />;
  }

  if (!institution) {
    return (
      <EmptyState
        title="Institución no encontrada"
        description="La institución solicitada no forma parte del catálogo público actual."
      />
    );
  }

  return (
    <div className="page">
      <DetailHeader
        eyebrow={institution.country || "Institución"}
        title={institution.name}
        description={institution.description}
        aside={
          <div className="detail-panel">
            <StatCard label="Programas" value={institution.programs_count || 0} />
            <StatCard label="Fundación" value={institution.founded || "Sin datos"} />
            <a
              className="button button-primary"
              href={institution.website_url || "#"}
              target="_blank"
              rel="noreferrer"
            >
              Visitar sitio
            </a>
          </div>
        }
      />

      <section className="detail-grid">
        <article className="panel">
          <h2>Ficha institucional</h2>
          <dl className="detail-list">
            <div>
              <dt>Ubicación</dt>
              <dd>{institution.location || "En línea"}</dd>
            </div>
            <div>
              <dt>Acreditación</dt>
              <dd>{institution.accreditation || "Consultar"}</dd>
            </div>
            <div>
              <dt>Sitio</dt>
              <dd>{institution.website || institution.website_url || "Consultar"}</dd>
            </div>
            {institution.sourceUrl ? (
              <div>
                <dt>Fuente</dt>
                <dd>
                  <a href={institution.sourceUrl} target="_blank" rel="noreferrer">
                    {institution.sourceLabel || "Sitio oficial"}
                  </a>
                </dd>
              </div>
            ) : null}
          </dl>
        </article>
        <article className="panel panel-media">
          <img src={institution.imageUrl} alt={institution.name} loading="lazy" />
        </article>
      </section>

      <section className="detail-grid detail-grid-opinions">
        <ApprovedOpinionList opinions={institution.opinions || []} />
        <OpinionForm
          entityType="institution"
          entityId={String(institution.id)}
          entityPath={institution.path || location.pathname}
          entityLabel={institution.name}
        />
      </section>

      <EntityEditorialSection
        profile={institution.editorial_profile}
        relatedContent={institution.related_content}
      />

      <section className="section-heading">
        <div>
          <span className="eyebrow">Programas</span>
          <h2>Oferta académica vinculada</h2>
        </div>
        <p>{programsState.meta?.total || 0} resultados asociados a esta institución.</p>
      </section>

      {programsState.loading && !programsState.data ? <LoadingBlock /> : null}
      {programsState.error ? <ErrorBlock message={programsState.error} /> : null}
      {programsState.data && programsState.data.length > 0 ? (
        <>
          <Grid>
            {programsState.data.map((program) => (
              <ProgramCard key={program.id} program={program} />
            ))}
          </Grid>
          <Pagination
            meta={programsState.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : (
        <EmptyState
          title="Sin programas publicados"
          description="Esta institución todavía no tiene programas visibles en el catálogo reconstruido."
        />
      )}
    </div>
  );
}

function InquiryModal({ programTitle, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course: programTitle,
  });
  const [status, setStatus] = useState({ loading: false, message: "", error: "" });

  useEffect(() => {
    setForm((current) => ({ ...current, course: programTitle }));
  }, [programTitle]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ loading: true, message: "", error: "" });

    try {
      const response = await fetch("/submit_form.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "No se pudo enviar la solicitud.");
      }

      setStatus({
        loading: false,
        message: "Solicitud enviada correctamente. Te contactaremos pronto.",
        error: "",
      });
      setTimeout(onClose, 1400);
    } catch (error) {
      setStatus({
        loading: false,
        message: "",
        error: error.message,
      });
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <button className="modal-close" type="button" onClick={onClose}>
          ×
        </button>
        <h2>Solicitar información</h2>
        <p>Completá el formulario y enviamos tu consulta al flujo existente.</p>
        <form className="modal-form" onSubmit={handleSubmit}>
          <label className="field">
            <span>Nombre</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
          </label>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              required
            />
          </label>
          <label className="field">
            <span>Teléfono</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
            />
          </label>
          <label className="field">
            <span>Programa</span>
            <input type="text" value={form.course} readOnly />
          </label>
          {status.message ? <p className="form-success">{status.message}</p> : null}
          {status.error ? <p className="form-error">{status.error}</p> : null}
          <button className="button button-primary" type="submit" disabled={status.loading}>
            {status.loading ? "Enviando..." : "Enviar solicitud"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProgramDetailPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const programKey = params.programSlug || params.id || params.slug;
  const programLabel = humanizeSlug(programKey);
  const programState = useApiData(`/programs/${encodeURIComponent(programKey)}`, {});
  const [showInquiry, setShowInquiry] = useState(false);
  const program = programState.data;

  useEffect(() => {
    if (program?.path && location.pathname !== program.path) {
      navigate(program.path + location.search, { replace: true });
    }
  }, [location.pathname, location.search, navigate, program]);

  useDocumentMeta(
    program ? `${program.title} | Cursos a Distancia` : `${programLabel || "Programa"} | Cursos a Distancia`,
    program
      ? buildProgramMetaDescription(program)
      : `Conocé ${programLabel || "este programa"} con modalidad, duración y precios en Argentina.`
  );

  const faqItems = buildProgramFaqItems(program);

  useStructuredData(
    "program-faq",
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null
  );

  if (programState.loading && !program) {
    return <LoadingBlock label="Cargando programa..." />;
  }

  if (programState.error) {
    return <ErrorBlock message={programState.error} />;
  }

  if (!program) {
    return (
      <EmptyState
        title="Programa no encontrado"
        description="El identificador solicitado no está disponible en el catálogo."
        action={
          <button className="button button-primary" type="button" onClick={() => navigate("/")}>
            Volver al directorio
          </button>
        }
      />
    );
  }

  const curriculum = program.programaCompleto || [];
  const requirements = program.requirements || [];
  const certifications = program.certifications || [];
  const programNarrative = program.htmlDescription || program.description;
  const communityRating = normalizeCommunityRating(program.community_rating);

  return (
    <div className="page">
      <DetailHeader
        eyebrow={program.area || "Programa"}
        title={program.title}
        description={program.description || excerpt(program.htmlDescription || "", 180)}
        aside={
          <div className="detail-panel">
            {communityRating ? (
              <div className="program-rating program-rating-detail" data-testid="program-rating-detail">
                <span className="program-rating-label">Calificación de la comunidad</span>
                <strong className="program-rating-value">
                  {formatCommunityRatingAverage(communityRating.average)}/5
                </strong>
                <p className="program-rating-summary">
                  {formatCommunityRatingCount(communityRating.count, true)}
                </p>
              </div>
            ) : null}
            <StatCard label="Institución" value={program.institution?.name || "-"} />
            <StatCard label="Duración" value={program.duration || "Consultar"} />
            <button className="button button-primary" type="button" onClick={() => setShowInquiry(true)}>
              Solicitar información
            </button>
          </div>
        }
      />

      <section className="detail-grid">
        <article className="panel panel-media">
          <img src={program.imageUrl} alt={buildProgramImageAlt(program)} loading="lazy" />
        </article>
        <article className="panel">
          <h2>Resumen del programa</h2>
          <dl className="detail-list">
            <div>
              <dt>Institución</dt>
              <dd>
                <Link to={getInstitutionPath(program.institution)}>{program.institution?.name}</Link>
              </dd>
            </div>
            <div>
              <dt>Modalidad</dt>
              <dd>{program.modality || "En línea"}</dd>
            </div>
            <div>
              <dt>Nivel</dt>
              <dd>{program.level || "Consultar"}</dd>
            </div>
            <div>
              <dt>Idioma</dt>
              <dd>{program.language || "Consultar"}</dd>
            </div>
            <div>
              <dt>Inicio</dt>
              <dd>{program.startDate || "Consultar"}</dd>
            </div>
            <div>
              <dt>Precio</dt>
              <dd>{program.price || "Consultar"}</dd>
            </div>
            {program.sourceUrl ? (
              <div>
                <dt>Fuente</dt>
                <dd>
                  <a href={program.sourceUrl} target="_blank" rel="noreferrer">
                    {program.sourceLabel || "Sitio oficial"}
                  </a>
                </dd>
              </div>
            ) : null}
          </dl>
        </article>
      </section>

      <section className="detail-grid">
        <article className="panel">
          <span className="eyebrow">Vista general</span>
          <h2>De qué trata este programa</h2>
          {programNarrative ? (
            <div
              className="prose-copy"
              dangerouslySetInnerHTML={{ __html: programNarrative }}
            />
          ) : (
            <p>
              Esta ficha resume la información pública disponible para entender el enfoque del
              programa y compararlo con otras alternativas del directorio.
            </p>
          )}
        </article>
        <article className="panel">
          <span className="eyebrow">Antes de decidir</span>
          <h2>Qué conviene revisar en esta propuesta</h2>
          <ul className="rich-list">
            <li>
              Confirmá si la modalidad {program.modality || "online"} encaja con tu ritmo de
              estudio y con el tipo de cursada que buscás.
            </li>
            <li>
              Revisá si la duración {program.duration || "informada por la institución"} y el nivel{" "}
              {program.level || "del programa"} coinciden con tu punto de partida.
            </li>
            <li>
              Antes de avanzar, verificá requisitos, certificaciones y el plan de estudios completo
              desde la fuente oficial.
            </li>
          </ul>
          <div className="link-stack">
            <Link to={getInstitutionPath(program.institution)}>
              Ver más programas de {program.institution?.name || "esta institución"}
            </Link>
            {program.area ? (
              <Link to={`/?area=${encodeURIComponent(program.area)}`}>
                Explorar más opciones de {program.area}
              </Link>
            ) : null}
            <Link to="/comparador">Comparar este programa con otras alternativas</Link>
          </div>
        </article>
      </section>

      <section className="detail-grid detail-grid-opinions">
        <ApprovedOpinionList opinions={program.opinions || []} />
        <OpinionForm
          entityType="program"
          entityId={String(program.id)}
          entityPath={program.path || location.pathname}
          entityLabel={program.title}
        />
      </section>

      <section className="section-heading">
        <div>
          <span className="eyebrow">Detalle</span>
          <h2>Contenido académico</h2>
        </div>
      </section>

      <section className="detail-grid detail-grid-stack">
        <article className="panel">
          <h3>Programa completo</h3>
          {curriculum.length > 0 ? (
            <ul className="rich-list">
              {curriculum.map((item) => (
                <li key={item}>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          ) : (
            <p>Sin temario expandido para este programa.</p>
          )}
        </article>
        <article className="panel">
          <h3>Requisitos</h3>
          {requirements.length > 0 ? (
            <ul className="rich-list">
              {requirements.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p>No hay requisitos cargados.</p>
          )}
        </article>
        <article className="panel">
          <h3>Certificaciones</h3>
          {certifications.length > 0 ? (
            <ul className="rich-list">
              {certifications.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p>No hay certificaciones específicas cargadas.</p>
          )}
        </article>
      </section>

      <section className="panel">
        <span className="eyebrow">Preguntas frecuentes</span>
        <h2>FAQ del programa</h2>
        <div className="faq-list">
          {faqItems.map((item) => (
            <div key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <EntityEditorialSection
        profile={program.editorial_profile}
        relatedContent={program.related_content}
      />

      {showInquiry ? (
        <InquiryModal programTitle={program.title} onClose={() => setShowInquiry(false)} />
      ) : null}
    </div>
  );
}

function CountriesPage() {
  const countriesState = useApiData("/countries", {});

  useDocumentMeta(
    "Países del catálogo | Cursos a Distancia",
    "Navegá el catálogo por país con agrupaciones listas para directorios amplios."
  );

  return (
    <div className="page">
      <Hero
        eyebrow="Países"
        title="Agrupaciones por país"
        description="Cada país mantiene su propia ruta pública y lista de instituciones sin depender de datos embebidos."
        aside={
          <div className="stats-grid">
            <StatCard label="Países" value={countriesState.data?.length || "-"} />
          </div>
        }
      />

      {countriesState.loading && !countriesState.data ? <LoadingBlock /> : null}
      {countriesState.error ? <ErrorBlock message={countriesState.error} /> : null}
      {countriesState.data ? (
        <Grid>
          {countriesState.data.map((country) => (
            <CountryCard key={country.slug} country={country} />
          ))}
        </Grid>
      ) : null}
    </div>
  );
}

function CountryDetailPage() {
  const { countrySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = clampPage(searchParams.get("page"), 1);
  const countriesState = useApiData("/countries", {});
  const country = (countriesState.data || []).find((item) => item.slug === countrySlug);
  const institutionsState = useApiData(
    "/institutions",
    country ? { country: country.name, page, page_size: 12 } : {}
  );

  useDocumentMeta(
    country ? `${country.name} | Instituciones online` : "País | Cursos a Distancia",
    country
      ? `Explorá instituciones y programas online disponibles en ${country.name}.`
      : DEFAULT_DESCRIPTION
  );

  if (countriesState.loading && !countriesState.data) {
    return <LoadingBlock label="Cargando país..." />;
  }

  if (!country) {
    return (
      <EmptyState
        title="País no encontrado"
        description="La ruta solicitada no coincide con un país disponible en el catálogo."
      />
    );
  }

  return (
    <div className="page">
      <Hero
        eyebrow="País"
        title={country.name}
        description={`Este país agrupa ${country.institutions_count} instituciones y ${country.programs_count} programas dentro del catálogo público.`}
        aside={
          <div className="stats-grid">
            <StatCard label="Instituciones" value={country.institutions_count} />
            <StatCard label="Programas" value={country.programs_count} />
          </div>
        }
      />

      {institutionsState.loading && !institutionsState.data ? <LoadingBlock /> : null}
      {institutionsState.error ? <ErrorBlock message={institutionsState.error} /> : null}
      {institutionsState.data ? (
        <>
          <Grid>
            {institutionsState.data.map((institution) => (
              <InstitutionCard key={institution.id} institution={institution} />
            ))}
          </Grid>
          <Pagination
            meta={institutionsState.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : null}
    </div>
  );
}

function ComparePage() {
  const metaState = useCatalogMeta();
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const compareResults = useApiData("/programs", {
    page: 1,
    page_size: 6,
    q: deferredQuery,
  });
  const [selectedPrograms, setSelectedPrograms] = useState([]);

  useDocumentMeta(
    "Comparador de programas | Cursos a Distancia",
    "Armá una vista comparativa rápida de programas usando resultados del API paginado."
  );

  const toggleProgram = (program) => {
    setSelectedPrograms((current) => {
      const exists = current.some((item) => item.id === program.id);
      if (exists) {
        return current.filter((item) => item.id !== program.id);
      }

      if (current.length >= 3) {
        return [...current.slice(1), program];
      }

      return [...current, program];
    });
  };

  return (
    <div className="page">
      <Hero
        eyebrow="Comparador"
        title="Compará programas sin salir del directorio"
        description="Esta vista usa el mismo endpoint paginado de programas y permite sostener un comparador liviano sin cargar todo el catálogo en memoria."
        aside={
          <div className="stats-grid">
            <StatCard label="En comparación" value={selectedPrograms.length} />
            <StatCard label="Programas totales" value={metaState.data?.counts?.programs || "-"} />
          </div>
        }
      />

      <FiltersBar>
        <QueryInput
          label="Buscar programa"
          value={query}
          onChange={setQuery}
          placeholder="Probá con UX, data, marketing..."
        />
      </FiltersBar>

      {selectedPrograms.length > 0 ? (
        <section className="comparison-table">
          {selectedPrograms.map((program) => (
            <article className="comparison-card" key={program.id}>
              <h3>{program.title}</h3>
              <p>{program.institution_name}</p>
              <dl className="detail-list">
                <div>
                  <dt>Área</dt>
                  <dd>{program.area || "Consultar"}</dd>
                </div>
                <div>
                  <dt>Nivel</dt>
                  <dd>{program.level || "Consultar"}</dd>
                </div>
                <div>
                  <dt>Duración</dt>
                  <dd>{program.duration || "Consultar"}</dd>
                </div>
                <div>
                  <dt>Precio</dt>
                  <dd>{program.price || "Consultar"}</dd>
                </div>
              </dl>
              <button
                className="button button-secondary"
                type="button"
                onClick={() => toggleProgram(program)}
              >
                Quitar
              </button>
            </article>
          ))}
        </section>
      ) : null}

      {compareResults.loading && !compareResults.data ? <LoadingBlock /> : null}
      {compareResults.data ? (
        <Grid>
          {compareResults.data.map((program) => {
            const isSelected = selectedPrograms.some((item) => item.id === program.id);

            return (
              <ProgramCard
                key={program.id}
                program={program}
                action={
                  <button
                    className={`button ${isSelected ? "button-secondary" : "button-ghost"}`}
                    type="button"
                    onClick={() => toggleProgram(program)}
                  >
                    {isSelected ? "Quitar" : "Comparar"}
                  </button>
                }
              />
            );
          })}
        </Grid>
      ) : null}
    </div>
  );
}

function BlogPage() {
  return <EditorialHubPage />;
}

function NotFoundPage() {
  useDocumentMeta("Página no encontrada | Cursos a Distancia", DEFAULT_DESCRIPTION);

  return (
    <div className="page">
      <EmptyState
        title="Página no encontrada"
        description="La URL solicitada no existe dentro del directorio."
        action={
          <Link className="button button-primary" to="/">
            Volver al inicio
          </Link>
        }
      />
    </div>
  );
}

export default function App() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<DirectoryPage />} />
        <Route path="/instituciones" element={<InstitutionsPage />} />
        <Route path="/instituciones/:id" element={<InstitutionDetailPage />} />
        <Route path="/:institutionSlug" element={<InstitutionDetailPage />} />
        <Route path="/paises" element={<CountriesPage />} />
        <Route path="/paises/:countrySlug" element={<CountryDetailPage />} />
        <Route path="/programa/:id" element={<ProgramDetailPage />} />
        <Route path="/:institutionSlug/:programSlug" element={<ProgramDetailPage />} />
        <Route path="/comparador" element={<ComparePage />} />
        <Route path="/resenas" element={<ReviewsPage />} />
        <Route path="/resenas/:slug" element={<ReviewDetailPage />} />
        <Route path="/comparativas" element={<ComparisonsPage />} />
        <Route path="/comparativas/:slug" element={<ComparisonDetailPage />} />
        <Route path="/mejores" element={<ListiclesPage />} />
        <Route path="/mejores/:slug" element={<ListicleDetailPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Shell>
  );
}
